import { Link } from 'react-router'

import { Card, RecordingStatusPill } from '../ui'
import type { RecordingRecord } from '../../lib/api'
import { formatSessionDate } from '../../lib/format'
import { CONDITION_WORDS } from '../../lib/recordings'

/**
 * The most recently uploaded recordings, in the order the list gave them.
 *
 * ── LIST ORDER, AND WHY IT IS NOT RE-SORTED ─────────────────────────────────
 *
 * `GET /users/{id}/recordings` pages newest-first by `created_at`, so page one
 * is an exact prefix in *upload* order and "the most recently uploaded" is true
 * however long the history is. Re-sorting one page by `session_date` would make
 * the caption claim "most recently recorded", which a single page cannot know:
 * a backfilled upload on page two can be later by recording date than anything
 * here. Today's own header comment makes the same argument the other way round.
 *
 * ── WHAT THIS CARD IS FOR BESIDES BEING A LIST ──────────────────────────────
 *
 * It is also how a failed attempt made *after* the latest result stays visible
 * on Home (§1.1's second half): the rows carry their own outcome pills, so a
 * rejected upload is on the screen next to the good result rather than behind
 * it. The pill is `RecordingStatusPill`, which is where `empty` reads
 * "No signal recorded" rather than "failed".
 *
 * The caller decides the cut and writes the caption; this renders what it is
 * given. Rows are not links — History is one click away in the head, and a list
 * of five identical-looking link targets is worse navigation than one.
 */
export function RecentRecordings({
  rows,
  caption,
}: {
  rows: readonly RecordingRecord[]
  /** The truncation sentence, or `null` when the list is all there is. */
  caption: string | null
}) {
  return (
    <Card
      eyebrow="Recent recordings"
      head={
        <Link className="btn btn--ghost btn--sm" to="/history">
          All history
        </Link>
      }
    >
      <ul className="recent">
        {rows.map((row) => (
          <li className="recent__row" key={row.recording_id}>
            {/* `formatSessionDate`, never `formatDate`: `session_date` is a
                plain calendar date, and `new Date("2026-09-10")` is UTC
                midnight — which renders as the 9th in America/New_York. */}
            <span className="recent__lead mono-cell">{formatSessionDate(row.session_date)}</span>
            <span className="recent__body">
              {CONDITION_WORDS[row.condition]} <span className="mono-cell">{row.condition}</span>
            </span>
            <span className="recent__end">
              <RecordingStatusPill status={row.status} />
            </span>
          </li>
        ))}
      </ul>
      {caption !== null && <p className="home-note">{caption}</p>}
    </Card>
  )
}
